import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * AITextEditor - High Performance Text Reveal Component
 *
 * Features:
 * 1. Gray gradient sweep from left to right (scanning phase)
 * 2. Smooth crossfade morph (changing phase)
 * 3. Text changes permanently to new content
 *
 * Performance: Uses CSS clip-path instead of per-character rendering
 * for smooth 60fps animations without browser freezing.
 *
 * @example
 * ```tsx
 * const [blocks, setBlocks] = useState([
 *   { id: '1', content: 'Original text', type: 'paragraph' }
 * ]);
 * const [activeIndex, setActiveIndex] = useState(-1);
 *
 * <AITextEditor
 *   blocks={blocks}
 *   activeBlockIndex={activeIndex}
 *   refinedContent={{ '1': 'Refined text by AI' }}
 * />
 * ```
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RichTextMorph } from './RichTextMorph';
// ============================================
// ANIMATION UTILITY (no external deps)
// ============================================
function animateValue(from, to, duration, onUpdate, onComplete) {
    const startTime = performance.now();
    let cancelled = false;
    function tick(currentTime) {
        if (cancelled)
            return;
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // Ease out cubic
        const eased = 1 - Math.pow(1 - progress, 3);
        const value = from + (to - from) * eased;
        onUpdate(value);
        if (progress < 1) {
            requestAnimationFrame(tick);
        }
        else {
            onComplete?.();
        }
    }
    requestAnimationFrame(tick);
    return {
        stop: () => { cancelled = true; }
    };
}
// ============================================
// PERFORMANT GRADIENT SWEEP - Uses CSS clip-path
// ============================================
const GradientSweepLine = React.memo(({ text, progress, isActive, }) => {
    const revealPercent = progress * 100;
    return (_jsxs("span", { className: "relative inline-block w-full", children: [_jsx("span", { className: "text-slate-400 dark:text-slate-500", style: {
                    opacity: isActive ? 0.3 : 0,
                    transition: 'opacity 0.3s'
                }, children: text }), _jsx("span", { className: "absolute inset-0 text-inherit", style: {
                    clipPath: `inset(0 ${100 - revealPercent}% 0 0)`,
                    willChange: isActive ? 'clip-path' : 'auto',
                }, children: text }), isActive && progress < 1 && (_jsx("span", { className: "absolute top-0 bottom-0 w-12 pointer-events-none", style: {
                    left: `${revealPercent}%`,
                    transform: 'translateX(-50%)',
                    background: 'linear-gradient(90deg, transparent, rgba(100, 116, 139, 0.25), transparent)',
                } }))] }));
});
GradientSweepLine.displayName = 'GradientSweepLine';
// ============================================
// PERFORMANT MORPH - Crossfade with clip-path
// ============================================
const PerformantMorph = React.memo(({ oldText, newText, speed = 1, onComplete, }) => {
    const [progress, setProgress] = useState(0);
    const hasStarted = useRef(false);
    useEffect(() => {
        if (hasStarted.current)
            return;
        hasStarted.current = true;
        const controls = animateValue(0, 1, 1000 / speed, setProgress, () => {
            setProgress(1);
            onComplete?.();
        });
        return () => controls.stop();
    }, [speed, onComplete]);
    const revealPercent = progress * 100;
    return (_jsxs("span", { className: "relative inline-block w-full", children: [_jsx("span", { className: "text-slate-500 dark:text-slate-400", style: {
                    opacity: 1 - progress,
                    transition: 'none',
                }, children: oldText }), _jsx("span", { className: "absolute inset-0 text-inherit", style: {
                    clipPath: `inset(0 ${100 - revealPercent}% 0 0)`,
                    willChange: 'clip-path',
                }, children: newText }), progress < 1 && (_jsx("span", { className: "absolute top-0 bottom-0 w-8 pointer-events-none", style: {
                    left: `${revealPercent}%`,
                    transform: 'translateX(-50%)',
                    background: 'linear-gradient(90deg, transparent, rgba(100, 116, 139, 0.3), transparent)',
                } }))] }));
});
PerformantMorph.displayName = 'PerformantMorph';
// ============================================
// ANIMATED BLOCK
// ============================================
const AnimatedBlock = React.memo(({ originalContent, newContent, type, isActive, hasCompleted, speed = 1, }) => {
    const [scanProgress, setScanProgress] = useState(0);
    const [phase, setPhase] = useState('idle');
    const hasChange = !!newContent;
    const scanStarted = useRef(false);
    const controlsRef = useRef(null);
    // Handle scanning phase
    useEffect(() => {
        if (isActive && phase === 'idle' && !scanStarted.current) {
            scanStarted.current = true;
            setPhase('scanning');
            setScanProgress(0);
            controlsRef.current = animateValue(0, 1, 1600 / speed, setScanProgress, () => {
                if (hasChange) {
                    setPhase('morphing');
                }
                else {
                    setPhase('done');
                }
            });
        }
        return () => {
            controlsRef.current?.stop();
        };
    }, [isActive, phase, hasChange, speed]);
    // When completed externally
    useEffect(() => {
        if (!isActive && hasCompleted && phase !== 'done') {
            setPhase('done');
            setScanProgress(1);
        }
    }, [isActive, hasCompleted, phase]);
    const handleMorphComplete = useCallback(() => {
        setPhase('done');
    }, []);
    // Style by type
    const getTypeClasses = () => {
        switch (type) {
            case 'heading':
                return 'text-xl font-semibold text-slate-900 dark:text-white';
            case 'code':
                return 'font-mono text-sm bg-slate-50 dark:bg-slate-800/50 px-4 py-3 rounded-lg';
            default:
                return 'text-base text-slate-700 dark:text-slate-300 leading-relaxed';
        }
    };
    // Determine content to show
    const renderContent = () => {
        if (phase === 'done' && hasChange) {
            // Parse and render final content with links
            return _jsx(RichTextMorph, { oldText: newContent, newText: newContent, speed: speed });
        }
        if (phase === 'morphing' && hasChange) {
            // Use RichTextMorph with blur effect for elegant text morphing
            return (_jsx(RichTextMorph, { oldText: originalContent, newText: newContent, speed: speed, onComplete: handleMorphComplete, showLinkHighlights: true }));
        }
        return (_jsx(GradientSweepLine, { text: originalContent, progress: scanProgress, isActive: phase === 'scanning' }));
    };
    return (_jsxs("div", { className: `relative py-3 px-3 rounded-lg ${getTypeClasses()}`, children: [(phase === 'scanning' || phase === 'morphing') && (_jsxs("div", { className: "absolute inset-0 overflow-hidden rounded-lg -z-10 pointer-events-none", children: [_jsx("div", { className: "absolute inset-y-0 w-40", style: {
                            left: `${scanProgress * 100}%`,
                            transform: 'translateX(-50%)',
                            background: 'linear-gradient(90deg, transparent, rgba(100, 116, 139, 0.1), rgba(148, 163, 184, 0.15), rgba(100, 116, 139, 0.1), transparent)',
                        } }), _jsx("div", { className: "absolute inset-0", style: {
                            background: `linear-gradient(90deg, rgba(100, 116, 139, 0.04) 0%, rgba(148, 163, 184, 0.03) ${scanProgress * 100}%, transparent ${scanProgress * 100}%)`,
                        } })] })), isActive && (_jsx("div", { className: "absolute -left-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-slate-400 dark:bg-slate-500" })), renderContent()] }));
});
AnimatedBlock.displayName = 'AnimatedBlock';
// ============================================
// MAIN COMPONENT
// ============================================
export function AITextEditor({ blocks, activeBlockIndex = -1, refinedContent = {}, completedBlockIds = new Set(), speed = 1, className = '', resetKey = 0, }) {
    return (_jsx("div", { className: `relative ${className}`, children: _jsx("div", { className: "space-y-2", children: blocks.map((block, index) => (_jsx(AnimatedBlock, { originalContent: block.content, newContent: refinedContent[block.id], type: block.type, isActive: index === activeBlockIndex, hasCompleted: completedBlockIds.has(block.id), speed: speed }, `${block.id}-${resetKey}`))) }) }));
}
export default AITextEditor;
